import { useState } from "react";
import { taskService } from "@miplatform/api";
import { DOCUMENT_TYPES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { EmailTemplateType } from "@miplatform/types";
import {
  createUUID,
  externalReferenceId,
  getRootUrl,
  getUniqueArrayByField,
} from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { getAttachmentsFromDocuments } from "./util/emailFormUtil";
import { CLOSEOUT_SCREENS } from "../../views/Schedule/Scheduler/CloseoutDrawer/constants";
import { filterIssuesWithHistoryOnAttempt } from "../../views/Schedule/utils";
import { getAttemptEmailTemplate } from "./EmailTemplate/emailTemplateUtil";
import { EMAIL_TEMPLATE_TYPE_TO_DRAWER_CONFIG } from "./emailFormConstants";

export const useEmailFormAttemptViewModel = ({
  account,
  entity,
  step,
  templateType,
  files,
  documentCategory,
  getValues,
  openCloseoutRef,
  onClose,
  onCloseoutDrawerOpen,
}) => {
  const { title, templateConfig, toast } =
    EMAIL_TEMPLATE_TYPE_TO_DRAWER_CONFIG[templateType];
  const { loading, uploadDocument } = useDocumentsViewModel();
  const [contacts, setContacts] = useState(null);
  const [isSent, setIsSent] = useState(false);
  const [attempt, setAttempt] = useState(entity);
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const getEmailContacts = ({ id, templateType }) =>
    stateManager.run(async () => {
      const response = await taskService.attempts.getEmailTemplateContacts({
        attemptId: id,
        emailType: templateType,
      });

      if (response.status === HTTP_STATUSES.OK) {
        const { to, cc, relatedContacts } = response.data;
        setContacts({
          to: getUniqueArrayByField(to, "email"),
          cc: getUniqueArrayByField(cc, "email"),
          relatedContacts: getUniqueArrayByField(relatedContacts, "email"),
        });
      }
    });

  const getAttempt = () =>
    stateManager.run(async () => {
      const { status, data } = await taskService.attempts.get(entity.id);

      if (status === HTTP_STATUSES.OK) {
        setAttempt({
          ...entity,
          ...data,
          issues: filterIssuesWithHistoryOnAttempt(data),
          unfilteredIssues: data.issues,
        });
      }
    });

  const sendEmail = async () => {
    const fields = getValues();
    const emailId = createUUID();
    let filePromises = [];

    if (files.length > 0) {
      const uploadPromises = files.map((file) =>
        uploadDocument({
          file,
          documentType: DOCUMENT_TYPES.UNKNOWN,
          category: documentCategory,
          externalReferenceId: externalReferenceId.getAttemptEmailRefId({
            orderId: attempt.orderId,
            assignmentId: attempt.assignment.id,
            attemptId: attempt.id,
            emailId,
          }),
        })
      );

      filePromises = await Promise.all(uploadPromises);
    }
    const attachments = getAttachmentsFromDocuments(filePromises, getRootUrl());
    const emailBody = {
      id: emailId,
      emailId,
      emailType: templateType,
      subject: fields.subject,
      attemptId: attempt.id,
      to: fields.to.map(({ name, email }) => ({
        ...(name && { name }),
        email,
      })),
      cc: fields.cc.map(({ name, email }) => ({
        ...(name && { name }),
        email,
      })),
      attachments: filePromises,
      body: getAttemptEmailTemplate({
        entity: attempt,
        fields,
        attachments,
        account,
        config: templateConfig,
      }),
    };

    stateManager.run(async () => {
      const response = await (step
        ? taskService.assignments.submitStepResult
        : taskService.attempts.sendEmail)({
        attemptId: attempt?.id,
        assignmentId: attempt?.assignment?.id,
        stepId: step?.id,
        stepResultId: step?.stepResult?.id,
        data: step ? { EmailPayload: emailBody } : emailBody,
        toastMessageConfig: {
          success: {
            text: `${title} Sent`,
            subtext: toast,
          },
          error: {
            text: `${title} Failed`,
            subtext: `Your ${title.toLowerCase()} was not sent, please try again`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        if (
          templateType === EmailTemplateType.CloseoutAndCompletionNotice &&
          !isSent
        ) {
          closeoutAttempt();
        } else {
          onClose();
          if (openCloseoutRef.current) {
            onCloseoutDrawerOpen(attempt, {
              screen: CLOSEOUT_SCREENS.PREBILL,
            });
            openCloseoutRef.current = false;
          }
        }
      }
    });
  };

  const closeoutAttempt = () => {
    stateManager.run(async () => {
      const { status } = await taskService.attempts.closeout({
        data: { attemptId: attempt.id },
        modifiedOn: attempt.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Attempt closed successfully.",
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        onClose();
        if (openCloseoutRef.current) {
          onCloseoutDrawerOpen(attempt, {
            screen: CLOSEOUT_SCREENS.PREBILL,
          });
          openCloseoutRef.current = false;
        }
      }
    });
  };

  const getEmailHistory = () =>
    stateManager.run(async () => {
      const response = await taskService.attempts.getEmailHistory({
        attemptId: entity.id,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setIsSent(
          response.data.some((email) => email.emailType === templateType)
        );
      }
    });

  return {
    isLoading: loading || isLoading,
    isSent,
    contacts,
    entity: attempt,
    getEmailContacts,
    getEmailHistory,
    getEntity: getAttempt,
    sendEmail,
  };
};
